import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import "../styles/LogConsole.css";

const ANSI_REGEX = new RegExp(`${String.fromCharCode(27)}\\[[0-9;]*m`, "g");
const GREEN_ANSI_REGEX = new RegExp(`${String.fromCharCode(27)}\\[(?:92|32|1;32)m`);
const RED_ANSI_REGEX = new RegExp(`${String.fromCharCode(27)}\\[(?:91|31|1;31)m`);

const stripAnsi = (text = "") => String(text).replace(ANSI_REGEX, "");
const normalizeText = (text = "") =>
  stripAnsi(text)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const getLevelClass = (entry) => {
  const level = String(entry?.level || "").toUpperCase();
  if (level.includes("ERROR") || level.includes("CRITICAL")) return "error";
  if (level.includes("WARNING") || level.includes("WARN")) return "warning";
  if (level.includes("DEBUG")) return "debug";
  return "info";
};

const positiveSignals = [
  /\bganancia registrada\b/,
  /\bventa por tp\b/,
  /\btp ejecutado\b/,
  /\bganancia:\s*\$?\+?\d/,
  /\bneto(?: total)?:\s*\+?\$?\d/,
  /\bretorno:\s*\+/,
  /\bpnl[^-\n]*\+/,
  /\bganancias acumuladas\b/,
];

const negativeSignals = [
  /\bperdida registrada\b/,
  /\bventa por sl\b/,
  /\bsl ejecutado\b/,
  /\bperdida:\s*-?\$?\d/,
  /\bneto(?: total)?:\s*-\$?\d/,
  /\bretorno:\s*-/,
  /\bpnl[^\n]*-\$?\d/,
  /\bperdidas acumuladas\b/,
];

const getOutcomeClass = (rawMessage) => {
  const message = normalizeText(rawMessage);

  if (GREEN_ANSI_REGEX.test(rawMessage)) return "profit";
  if (RED_ANSI_REGEX.test(rawMessage)) return "loss";

  if (positiveSignals.some((pattern) => pattern.test(message))) return "profit";
  if (negativeSignals.some((pattern) => pattern.test(message))) return "loss";
  return "";
};

const enrichLog = (entry, idx) => ({
  key: `${entry.timestamp || "no-ts"}-${idx}`,
  text: stripAnsi(entry.message || ""),
  cls: `${getLevelClass(entry)} ${getOutcomeClass(entry.message || "")}`.trim(),
});

const ConsoleBody = React.memo(({ lines, bodyRef, autoScroll, onScroll, emptyText }) => (
  <div className="log-console-body" ref={bodyRef} onScroll={onScroll}>
    {!lines.length && <div className="log-console-empty">{emptyText}</div>}
    {lines.map((line) => (
      <pre key={line.key} className={`log-console-line ${line.cls}`}>{line.text}</pre>
    ))}
  </div>
));

const LogConsole = ({ logs = [], onClear }) => {
  const bodyRef = useRef(null);
  const modalBodyRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [modalAutoScroll, setModalAutoScroll] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const lastScrollTop = useRef(0);
  const modalLastScrollTop = useRef(0);

  const lines = useMemo(() => logs.map(enrichLog), [logs]);

  useEffect(() => {
    if (!autoScroll || !bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [lines, autoScroll]);

  useEffect(() => {
    if (!expanded || !modalAutoScroll || !modalBodyRef.current) return;
    modalBodyRef.current.scrollTop = modalBodyRef.current.scrollHeight;
  }, [lines, modalAutoScroll, expanded]);

  const handleScroll = useCallback(() => {
    if (!bodyRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 24;
    const scrollingUp = scrollTop < lastScrollTop.current;
    lastScrollTop.current = scrollTop;
    if (scrollingUp) { setAutoScroll(false); return; }
    setAutoScroll(nearBottom);
  }, []);

  const handleModalScroll = useCallback(() => {
    if (!modalBodyRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = modalBodyRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 24;
    const scrollingUp = scrollTop < modalLastScrollTop.current;
    modalLastScrollTop.current = scrollTop;
    if (scrollingUp) { setModalAutoScroll(false); return; }
    setModalAutoScroll(nearBottom);
  }, []);

  const toggleAutoScroll = useCallback(() => {
    setAutoScroll((prev) => !prev);
    setModalAutoScroll((prev) => !prev);
  }, []);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) toggleExpanded();
  }, [toggleExpanded]);

  return (
    <>
      <div className="log-console-widget">
        <div className="log-console-header">
          <h2>🖥️ Consola en Tiempo Real</h2>
          <div className="log-console-actions">
            <span className="log-console-count">{logs.length}</span>
            <button
              type="button"
              className={`log-console-autoscroll ${autoScroll ? "on" : "off"}`}
              onClick={toggleAutoScroll}
            >
              {autoScroll ? "Auto" : "Manual"}
            </button>
            <button
              type="button"
              className="log-console-expand"
              onClick={toggleExpanded}
              title="Ampliar"
            >
              ⛶
            </button>
            <button
              type="button"
              className="log-console-clear"
              onClick={() => onClear?.()}
              disabled={!logs.length}
            >
              Limpiar
            </button>
          </div>
        </div>

        <ConsoleBody
          lines={lines}
          bodyRef={bodyRef}
          autoScroll={autoScroll}
          onScroll={handleScroll}
          emptyText="Esperando logs del backend..."
        />
      </div>

      {expanded && (
        <div className="log-console-backdrop" onClick={handleBackdropClick}>
          <div className="log-console-modal">
            <div className="log-console-modal-header">
              <h3>📋 Consola — Vista Ampliada</h3>
              <div className="log-console-modal-actions">
                <span className="log-console-count">{logs.length}</span>
                <button
                  type="button"
                  className={`log-console-autoscroll ${modalAutoScroll ? "on" : "off"}`}
                  onClick={() => setModalAutoScroll((prev) => !prev)}
                >
                  {modalAutoScroll ? "Auto" : "Manual"}
                </button>
                <button
                  type="button"
                  className="log-console-modal-close"
                  onClick={toggleExpanded}
                >
                  ✕
                </button>
              </div>
            </div>
            <ConsoleBody
              lines={lines}
              bodyRef={modalBodyRef}
              autoScroll={modalAutoScroll}
              onScroll={handleModalScroll}
              emptyText="Esperando logs del backend..."
            />
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(LogConsole);
